// Working-together UI: what people see when two of them edit the same
// rundown at once, the version history (with restore), and short toasts for
// refused saves. Driven by events from src/platform/api.js.
import { Api } from "../../platform/api.js";
import { useT } from "../i18n.jsx";
import { AlertIcon, HistoryIcon, T } from "../theme.jsx";
import { Button, Modal, Notice, fmtTime } from "./ui.jsx";

const { useEffect, useState, useCallback } = React;

// Editors call this so "use their latest" / "restore" can make them reload,
// and "keep mine" can mark them saved again.
function useDocEvents(col, id, { onReload, onSaved, onConflict } = {}) {
  const handlers = React.useRef({});
  handlers.current = { onReload, onSaved, onConflict };
  useEffect(() => {
    if (!col || !id) return undefined;
    const match = (e) => e.detail && e.detail.col === col && e.detail.id === id;
    const on = (name) => (e) => { const h = handlers.current[name]; if (match(e) && h) h(e.detail); };
    const r = on("onReload"), s = on("onSaved"), c = on("onConflict");
    window.addEventListener("rundown:reload-doc", r);
    window.addEventListener("rundown:saved", s);
    window.addEventListener("rundown:conflict", c);
    return () => {
      window.removeEventListener("rundown:reload-doc", r);
      window.removeEventListener("rundown:saved", s);
      window.removeEventListener("rundown:conflict", c);
    };
  }, [col, id]);
}

function ConflictDialog() {
  const { t, lang } = useT();
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const on = (e) => setConflict(e.detail);
    window.addEventListener("rundown:conflict", on);
    return () => window.removeEventListener("rundown:conflict", on);
  }, []);
  if (!conflict) return null;
  const cur = conflict.current;
  const who = (cur && cur.updatedBy && cur.updatedBy.name) || t("someoneElse");
  const body = t("conflictBody").replace("{name}", who).replace("{time}", fmtTime(cur && cur.updatedAt, lang));
  const close = () => { setConflict(null); setBusy(false); };
  return (
    <Modal title={t("conflictTitle")} onClose={null} width={480}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
        <AlertIcon size={22} color={T.danger} />
        <p style={{ margin: 0, fontSize: 14, color: T.text, lineHeight: 1.55 }}>{body}</p>
      </div>
      <p style={{ fontSize: 12.5, color: T.subtext, margin: "0 0 14px" }}>{t("conflictHint")}</p>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button variant="secondary" disabled={busy} onClick={() => { conflict.reload(); close(); }}>{t("conflictReload")}</Button>
        <Button disabled={busy} onClick={async () => {
          setBusy(true);
          try { await conflict.overwrite(); close(); } catch (e) { setBusy(false); }
        }}>{t("conflictOverwrite")}</Button>
      </div>
    </Modal>
  );
}

function Toasts() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  useEffect(() => {
    const push = (text, tone) => {
      const id = Math.random().toString(36).slice(2);
      setItems((prev) => [...prev.slice(-2), { id, text, tone }]);
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), 6000);
    };
    const onForbidden = (e) => push(`${t("forbiddenToast")}${e.detail && e.detail.message ? ` — ${e.detail.message}` : ""}`, "danger");
    const onToast = (e) => push(e.detail.text, e.detail.tone || "ok");
    window.addEventListener("rundown:forbidden", onForbidden);
    window.addEventListener("rundown:toast", onToast);
    return () => { window.removeEventListener("rundown:forbidden", onForbidden); window.removeEventListener("rundown:toast", onToast); };
  }, [t]);
  return (
    <div role="status" aria-live="polite" style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 130, display: "flex", flexDirection: "column", gap: 8, width: "min(520px, 92vw)", pointerEvents: "none" }}>
      {items.map((x) => (
        <div key={x.id} style={{ background: x.tone === "danger" ? T.danger : T.dark, color: "#fff", borderRadius: 10, padding: "10px 14px", fontSize: 13.5, fontWeight: 600, boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}>{x.text}</div>
      ))}
    </div>
  );
}
const toast = (text, tone) => window.dispatchEvent(new CustomEvent("rundown:toast", { detail: { text, tone } }));

function HistoryButton({ col, id, perms }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  if (!perms || !perms.canHistory || !id) return null;
  return (
    <>
      <button className="rb-btn" type="button" onClick={() => setOpen(true)}
        style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12.5, color: T.text, fontWeight: 600 }}>
        <HistoryIcon size={13} /> {t("historyBtn")}
      </button>
      {open && <HistoryModal col={col} id={id} onClose={() => setOpen(false)} />}
    </>
  );
}

function HistoryModal({ col, id, onClose }) {
  const { t, lang } = useT();
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [confirmV, setConfirmV] = useState(null);
  const load = useCallback(async () => {
    try { setEntries((await Api.history(col, id)).entries); } catch (e) { setError(e.message); setEntries([]); }
  }, [col, id]);
  useEffect(() => { load(); }, [load]);
  const restore = async (v) => {
    try {
      await Api.restore(col, id, v);
      window.dispatchEvent(new CustomEvent("rundown:reload-doc", { detail: { col, id } }));
      toast(t("restoredMsg"));
      onClose();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal title={t("historyTitle")} onClose={onClose} closeLabel={t("close")} width={560}>
      {error && <Notice>{error}</Notice>}
      {entries === null ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("loading")}</div>
        : entries.length === 0 ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("historyEmpty")}</div>
        : (
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map((e) => (
              <li key={`${e.version}-${e.replacedAt}`} style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>
                    v{e.version} · {fmtTime(e.editedAt, lang)}
                  </div>
                  <div style={{ fontSize: 12.5, color: T.subtext }}>
                    {t("historyEditedBy")} {(e.editedBy && e.editedBy.name) || "—"}
                    {e.reason === "delete"
                      ? ` · ${t("reason_delete")}`
                      : ` · ${t("historyReplacedBy")} ${(e.replacedBy && e.replacedBy.name) || "—"} ${fmtTime(e.replacedAt, lang)}`}
                  </div>
                </div>
                {confirmV === e.version ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12.5, color: T.text, maxWidth: 220 }}>{t("restoreConfirm")}</span>
                    <Button variant="danger" onClick={() => restore(e.version)}>{t("restoreBtn")}</Button>
                    <Button variant="ghost" onClick={() => setConfirmV(null)}>{t("cancel")}</Button>
                  </div>
                ) : (
                  <Button variant="secondary" onClick={() => setConfirmV(e.version)}>{t("restoreBtn")}</Button>
                )}
              </li>
            ))}
          </ol>
        )}
    </Modal>
  );
}

export { useDocEvents, ConflictDialog, Toasts, toast, HistoryButton, HistoryModal };
